/**
 * Web Audio API のコンテキスト管理と共通ユーティリティ
 * 音声ファイルは使わず全音を手続き型生成する
 */
export class AudioManager {
  readonly ctx: AudioContext;
  readonly master: GainNode;

  private muted = false;
  private preMuteVolume = 0.7;

  constructor() {
    // AudioContextはsuspended状態で作成。resume()が呼ばれるまで音は出ない
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.7;
    this.master.connect(this.ctx.destination);
  }

  /** ゲーム開始時（Pointer Lock取得後）に呼ぶ */
  resume(): void {
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  get now(): number { return this.ctx.currentTime; }

  /** ミュートON/OFF切り替え。戻り値=現在のミュート状態 */
  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.muted) {
      const cur = this.master.gain.value;
      if (cur > 0.01) this.preMuteVolume = cur;
      this.master.gain.setTargetAtTime(0, this.now, 0.05);
    } else {
      this.master.gain.setTargetAtTime(this.preMuteVolume, this.now, 0.05);
    }
    return this.muted;
  }

  get isMuted(): boolean { return this.muted; }

  /** ポーズ時: ミュート状態に関わらず音量を0にフェードアウト */
  pauseAudio(): void {
    this.master.gain.setTargetAtTime(0, this.now, 0.1);
  }

  /** ポーズ解除時: ユーザーのミュート状態を維持しながら音量を復元 */
  resumeAudio(): void {
    const target = this.muted ? 0 : this.preMuteVolume;
    this.master.gain.setTargetAtTime(target, this.now, 0.15);
  }

  /** ホワイトノイズバッファを生成（使い回し可） */
  createNoiseBuffer(durationSec = 1.0): AudioBuffer {
    const sr     = this.ctx.sampleRate;
    const frames = Math.floor(sr * durationSec);
    const buf    = this.ctx.createBuffer(1, frames, sr);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  /** ノイズソースを返す（play後は自動停止） */
  createNoise(buf?: AudioBuffer): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = buf ?? this.createNoiseBuffer(0.5);
    return src;
  }

  createOsc(type: OscillatorType, freq: number): OscillatorNode {
    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    return osc;
  }

  createGain(value: number): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = value;
    return g;
  }

  createFilter(type: BiquadFilterType, freq: number, q = 1): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    return f;
  }

  /** マスター音量 (0〜1) */
  setMasterVolume(v: number): void {
    this.master.gain.setTargetAtTime(v, this.now, 0.05);
  }
}
